// ✅ src/pages/vehicle/LubeConsumptionAlert_Print.jsx
// Print page UI redesigned to MATCH your LubeRequirementReport_Print layout
// - Same header box + INFRA SCREEN box + bordered title + filter line + table style
// - Fetches from:  GET  /api/lubeConsumptionAlert/list
// - Reads filters from:
//    1) location.state.filters  (recommended)
//    2) fallback query params
//
// Route:
// <Route path="/vehicle/LubeConsumptionAlert_Print" element={<LubeConsumptionAlert_Print />} />

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/lubeConsumptionAlert";

/* ---------------- helpers ---------------- */
const toStr = (v, d = "") => (v === null || v === undefined || v === "" ? d : String(v));
const safe = (v, d = "-") => {
  const s = toStr(v, "");
  return s.trim() ? s : d;
};

const normalizeRow = (r) => {
  // supports your earlier mapping + possible SP changes
  return {
    branch: r.branch ?? r.fld_branchname ?? r.Branch ?? "",
    vehicleRegNo: r.vehicleRegNo ?? r.fld_vehregno ?? r.VehicleRegNo ?? "",
    vehicleName: r.vehicleName ?? r.fld_vehicle ?? r.VehicleName ?? "",
    vehicleCode: r.vehicleCode ?? r.fld_vehcode ?? r.VehicleCode ?? "",
    vehicleMainType: r.vehicleMainType ?? r.fld_vehiclemaintype ?? r.VehicleMainType ?? "",
    itemCategory: r.itemCategory ?? r.fld_itemcategory ?? r.ItemCategory ?? "",
    itemName: r.itemName ?? r.fld_itemname ?? r.ItemName ?? "",
    currentLevel: r.currentLevel ?? r.fld_current ?? r.CurrentLevel ?? "",
    threshold: r.threshold ?? r.fld_threshold ?? r.Threshold ?? "",
    status: r.status ?? r.fld_status ?? r.Status ?? "",
    lastRefill: r.lastRefill ?? r.fld_lastrefill ?? r.LastRefill ?? "",
    consumptionRate: r.consumptionRate ?? r.fld_consumptionrate ?? r.ConsumptionRate ?? "",
    raw: r,
  };
};

export default function LubeConsumptionAlert_Print() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sp] = useSearchParams();

  // ✅ prefer state filters (same as your LubeRequirementReport_Print)
  const stateFilters = location?.state?.filters || null;

  // ✅ fallback to query params (if opened directly)
  const queryFilters = useMemo(() => {
    return {
      vehtype: sp.get("vehtype") || "O",
      branchId: sp.get("branchId") || "Select",
      monthText: sp.get("monthText") || "",
      year: sp.get("year") || "",
      vehregno: sp.get("vehregno") || "%",
      vehname: sp.get("vehname") || "%",
      vehcode: sp.get("vehcode") || "%",
      vehmaintype: sp.get("vehmaintype") || "%",
      itemCategory: sp.get("itemCategory") || "%",
      itemName: sp.get("itemName") || "%",
      page: sp.get("page") || "1",
      pageSize: sp.get("pageSize") || "10000",
    };
  }, [sp]);

  // ✅ final filters used to fetch
  const filters = useMemo(() => {
    const f = stateFilters || queryFilters;
    return {
      vehtype: toStr(f?.vehtype, "O"),
      branchId: toStr(f?.branchId, "Select"),
      monthText: toStr(f?.monthText, ""),
      year: toStr(f?.year, ""),
      vehregno: toStr(f?.vehregno, "%"),
      vehname: toStr(f?.vehname, "%"),
      vehcode: toStr(f?.vehcode, "%"),
      vehmaintype: toStr(f?.vehmaintype, "%"),
      itemCategory: toStr(f?.itemCategory, "%"),
      itemName: toStr(f?.itemName, "%"),
      page: toStr(f?.page, "1"),
      pageSize: toStr(f?.pageSize, "10000"),
    };
  }, [stateFilters, queryFilters]);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // ✅ Fetch using /list ONLY
  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        // minimum validations (same spirit as your requirement print)
        if (!filters.branchId || !filters.monthText || !filters.year) {
          setErr("Print params missing (branchId/monthText/year). Please open print from report page.");
          return;
        }

        setLoading(true);
        setErr("");

        const res = await axios.get(`${API_BASE}/list`, { params: filters });

        if (!res?.data?.success) {
          throw new Error(res?.data?.message || "Failed to load print data");
        }

        const list = res?.data?.rows || [];
        if (!alive) return;

        setRows(list);
        setTotal(res?.data?.total ?? list.length);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [filters]);

  const normalized = useMemo(() => rows.map(normalizeRow), [rows]);

  const headerLine = useMemo(() => {
    const vehTypeText = filters.vehtype === "H" ? "Hire Vehicle" : "Own Vehicle";
    return `Vehicle Type : ${vehTypeText}   |   Branch : ${filters.branchId}   |   Month : ${filters.monthText}   |   Year : ${filters.year}`;
  }, [filters]);

  const filterLine2 = useMemo(() => {
    return `RegNo : ${safe(filters.vehregno)}   |   Veh Code : ${safe(filters.vehcode)}   |   Veh Name : ${safe(
      filters.vehname
    )}   |   Main Type : ${safe(filters.vehmaintype)}   |   Item Category : ${safe(filters.itemCategory)}   |   Item Name : ${safe(
      filters.itemName
    )}`;
  }, [filters]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      <style>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
        }
        @page { size: A4 landscape; margin: 0.3in; }
      `}</style>

      <div className="p-2">
        {/* Actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>

          <button
            onClick={() => window.print()}
            disabled={loading || !!err || normalized.length === 0}
            className={`px-5 py-2 text-white font-semibold rounded shadow ${
              loading || !!err || normalized.length === 0 ? "bg-gray-500" : "bg-blue-600 hover:bg-blue-700"
            }`}
            title={normalized.length === 0 ? "No rows to print" : "Print"}
          >
            🖨️ Print
          </button>
        </div>

        {/* Loading/Error */}
        {loading ? (
          <div className="no-print p-3 border rounded bg-gray-50 text-sm mb-2">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-3 border rounded bg-red-50 text-sm text-red-700 mb-2">{err}</div>
        ) : null}

        {/* Header box (same as your Lube Requirement print) */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
              </div>
              <div className="text-xs">
                Phone No : 9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
              </div>
            </div>

            <div className="w-44 text-right text-xs">
              <div className="font-semibold">INFRA SCREEN :</div>
              <div>B-61/2</div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">Lube Consumption Alert Report</h2>
        </div>

        {/* Filter lines */}
        <div className="border-2 border-black mb-2 p-1 text-xs">{headerLine}</div>
        <div className="border-2 border-black mb-2 p-1 text-xs">{filterLine2}</div>

        {/* Summary */}
        <div className="border-2 border-black mb-2 p-1 text-xs flex justify-between">
          <div>
            Total Rows : <b>{total}</b>
          </div>
          <div>
            Printed On : <b>{new Date().toLocaleString("en-IN")}</b>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 font-bold text-center">S.No</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Branch</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle RegNo</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Name</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Code</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Vehicle Main Type</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Item Category</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Item Name</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Current Level</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Threshold</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Status</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Last Refill</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Consumption Rate</th>
              </tr>
            </thead>

            <tbody>
              {normalized.length === 0 ? (
                <tr>
                  <td colSpan={13} className="border border-black px-2 py-2 text-center">
                    No data to print.
                  </td>
                </tr>
              ) : (
                normalized.map((x, i) => (
                  <tr key={i} className="bg-white">
                    <td className="border border-black px-1 py-1 text-center">{i + 1}</td>
                    <td className="border border-black px-1 py-1">{safe(x.branch, "")}</td>
                    <td className="border border-black px-1 py-1 text-center">{safe(x.vehicleRegNo, "")}</td>
                    <td className="border border-black px-1 py-1">{safe(x.vehicleName, "")}</td>
                    <td className="border border-black px-1 py-1 text-center">{safe(x.vehicleCode, "")}</td>
                    <td className="border border-black px-1 py-1">{safe(x.vehicleMainType, "")}</td>
                    <td className="border border-black px-1 py-1">{safe(x.itemCategory, "")}</td>
                    <td className="border border-black px-1 py-1">{safe(x.itemName, "")}</td>
                    <td className="border border-black px-1 py-1 text-center">{safe(x.currentLevel, "")}</td>
                    <td className="border border-black px-1 py-1 text-center">{safe(x.threshold, "")}</td>
                    <td className="border border-black px-1 py-1 text-center">{safe(x.status, "")}</td>
                    <td className="border border-black px-1 py-1 text-center">{safe(x.lastRefill, "")}</td>
                    <td className="border border-black px-1 py-1 text-center">{safe(x.consumptionRate, "")}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer (same as your requirement print) */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>
      </div>
    </div>
  );
}